import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { RechargeForm } from '../components/balance/RechargeForm';
import { ShamCashClaimForm } from '../components/balance/ShamCashClaimForm';
import { BinanceClaimForm } from '../components/balance/BinanceClaimForm';
import { CoinExClaimForm } from '../components/balance/CoinExClaimForm';
import { SyriatelClaimDialog } from '../components/balance/SyriatelClaimDialog';
import {
  useCreateDepositMutation,
  useClaimShamCashPaymentMutation,
  useClaimSyriatelPaymentMutation,
  useClaimBinancePaymentMutation,
  useGetBalanceQuery,
} from '../store/api/balanceApi';
import type { PaymentMethod, CreateDepositRequest, InvoiceDetails } from '../store/api/balanceApi';

// Payment method icon component
interface PaymentMethodIconProps {
  method: PaymentMethod;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

function PaymentMethodIcon({ method, className = '', size = 'md' }: PaymentMethodIconProps) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };
  
  const iconPath = `/${method}.png`;
  
  return (
    <img
      src={iconPath}
      alt={`${method} payment method`}
      className={`${sizeClasses[size]} object-contain ${className}`}
    />
  );
}

export function RechargePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [createDeposit, { isLoading }] = useCreateDepositMutation();
  const [claimShamCashPayment, { isLoading: isShamClaiming }] = useClaimShamCashPaymentMutation();
  const [claimSyriatelPayment, { isLoading: isClaiming }] = useClaimSyriatelPaymentMutation();
  const [claimBinancePayment, { isLoading: isBinanceClaiming }] = useClaimBinancePaymentMutation();
  const { refetch: refetchBalance } = useGetBalanceQuery();

  // Get payment method from URL or default to crypto
  // Note: Payeer is temporarily disabled but code is kept for future use
  const defaultMethod = (searchParams.get('method') || 'crypto') as PaymentMethod;
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(defaultMethod);
  const [invoice, setInvoice] = useState<InvoiceDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showClaimDialog, setShowClaimDialog] = useState(false);

  const handleSubmit = async (data: CreateDepositRequest) => {
    setError(null);
    if (selectedMethod === 'sham-cash') {
      return;
    }
    try {
      const result = await createDeposit({
        paymentMethod: selectedMethod,
        data,
      }).unwrap();
      
      setInvoice(result.data.invoice);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleMethodChange = (method: PaymentMethod) => {
    setSelectedMethod(method);
    setInvoice(null);
    setError(null);
  };

  const handleClaimShamCashPayment = async (transactionId: string, amount: number, currency: 'USD' | 'SYP') => {
    setError(null);
    try {
      const result = await claimShamCashPayment({ transactionId, amount, currency }).unwrap();
      toast.success(
        t('recharge.shamCashClaim.success', {
          amount: amount.toFixed(2),
          currency: currency,
        })
      );
      void refetchBalance();
      navigate('/balance', { replace: true });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      const message = error?.data?.message || error?.message || t('recharge.shamCashClaim.error');
      setError(message);
      toast.error(message);
      throw err;
    }
  };

  const handleClaimBinancePayment = async (transactionId: string) => {
    setError(null);
    try {
      const result = await claimBinancePayment({ transactionId }).unwrap();
      toast.success(
        t('recharge.binanceClaim.success', {
          amount: result.data.matchedAmount?.toFixed(2),
          currency: result.data.currency,
        })
      );
      void refetchBalance();
      navigate('/balance', { replace: true });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      const message = error?.data?.message || error?.message || t('recharge.binanceClaim.error');
      setError(message);
      toast.error(message);
      throw err;
    }
  };

  const handleClaimSyriatelPayment = async (transactionId: string) => {
    if (!invoice) return;

    try {
      const result = await claimSyriatelPayment({
        internalOrderId: invoice.invoiceId,
        transactionId,
      }).unwrap();

      if (result.data.claimed) {
        // Payment was claimed immediately
        toast.success(t('recharge.syriatelClaim.success', { amount: result.data.amountInUSD?.toFixed(2) }));
        void refetchBalance();
        // Optionally update invoice status
        setInvoice((prev) => prev ? { ...prev, status: 'completed' } : null);
      } else {
        // Awaiting webhook
        toast.info(t('recharge.syriatelClaim.pending', { message: result.data.message }));
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('recharge.syriatelClaim.error')
      );
      throw err; // Re-throw to let dialog handle it
    }
  };

  const getPaymentMethodLabel = (method: PaymentMethod): string => {
    const labels: Partial<Record<PaymentMethod, string>> = {
      // payeer: t('recharge.methods.payeer'), // Temporarily disabled but kept for future use
      crypto: t('recharge.methods.crypto'),
      syriatel: t('recharge.methods.syriatel'),
      'sham-cash': t('recharge.methods.shamCash'),
      binance: t('recharge.methods.binance'),
      coinex: t('recharge.methods.coinex'),
    };
    return labels[method] || method;
  };

  const renderInvoiceDetails = () => {
    if (!invoice) return null;

    return (
      <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mt-6 animate-scale-in">
        <h3 className="text-2xl font-heading font-bold text-brand-accent mb-4">
          {t('recharge.invoice.title')}
        </h3>

        {/* Invoice Info */}
        <div className="space-y-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-2">
            <span className="text-neutral-medium font-body text-sm sm:text-base">{t('recharge.invoice.invoiceId')}:</span>
            <span className="text-neutral-light font-mono font-body text-sm sm:text-base break-all">{invoice.invoiceId}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-2">
            <span className="text-neutral-medium font-body text-sm sm:text-base">{t('recharge.invoice.amount')}:</span>
            <span className="text-brand-accent font-heading font-bold text-sm sm:text-base">{invoice.amount.toFixed(2)} {invoice.currency}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-2">
            <span className="text-neutral-medium font-body text-sm sm:text-base">{t('recharge.invoice.status')}:</span>
            <span className={`px-3 py-1 rounded-full text-sm font-body ${
              invoice.status === 'pending' 
                ? 'bg-yellow-500/20 text-yellow-500' 
                : invoice.status === 'completed'
                ? 'bg-green-500/20 text-green-500'
                : 'bg-danger/20 text-danger'
            }`}>
              {invoice.status === 'pending' ? t('recharge.invoice.pending') : 
               invoice.status === 'completed' ? t('recharge.invoice.completed') : 
               t('recharge.invoice.failed')}
            </span>
          </div>
        </div>

        {/* Payment Instructions */}
        {invoice.paymentUrl && (
          <div className="mb-6">
            <p className="text-neutral-medium font-body mb-4">
              {typeof invoice.instructions === 'string' 
                ? invoice.instructions 
                : t('recharge.invoice.instructions')}
            </p>
            <a
              href={invoice.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 text-center"
            >
              {t('recharge.invoice.payNow')}
            </a>
          </div>
        )}

        {/* Crypto Details */}
        {invoice.invoiceDetails && (
          <div className="bg-neutral-dark/30 rounded-lg p-4 mb-6 space-y-2">
            <h4 className="text-lg font-heading font-semibold text-brand-accent mb-3">
              {t('recharge.invoice.cryptoDetails')}
            </h4>
            {invoice.invoiceDetails.network && (
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                <span className="text-neutral-medium font-body text-sm sm:text-base">{t('recharge.invoice.network')}:</span>
                <span className="text-neutral-light font-body text-sm sm:text-base">{invoice.invoiceDetails.network}</span>
              </div>
            )}
            {invoice.invoiceDetails.address && (
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                <span className="text-neutral-medium font-body text-sm sm:text-base">{t('recharge.invoice.address')}:</span>
                <span className="text-neutral-light font-mono text-sm font-body break-all">{invoice.invoiceDetails.address}</span>
              </div>
            )}
            {invoice.invoiceDetails.amount && (
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                <span className="text-neutral-medium font-body text-sm sm:text-base">{t('recharge.invoice.amount')}:</span>
                <span className="text-neutral-light font-body text-sm sm:text-base">{invoice.invoiceDetails.amount} {invoice.invoiceDetails.currency}</span>
              </div>
            )}
          </div>
        )}

        {/* Syriatel/Sham Cash Instructions */}
        {(() => {
          // Get receptionNumber from instructions object or direct property
          const instructions = invoice.instructions;
          const receptionNumber = 
            typeof instructions === 'object' && instructions?.receptionNumber
              ? instructions.receptionNumber
              : invoice.receptionNumber;
          const orderId = 
            typeof instructions === 'object' && instructions?.orderId
              ? instructions.orderId
              : invoice.orderId;
          const message = 
            typeof instructions === 'object' && instructions?.message
              ? instructions.message
              : invoice.message;

          if (!receptionNumber) return null;

          // Convert receptionNumber to array if it's a string
          const receptionNumbers = Array.isArray(receptionNumber) 
            ? receptionNumber 
            : typeof receptionNumber === 'string' 
            ? [receptionNumber] 
            : [];

          const handleCopyReceptionNumber = async (number: string) => {
            try {
              await navigator.clipboard.writeText(number);
              toast.success(t('common.copied'));
            } catch {
              toast.error(t('common.copyError') || t('errors.network'));
            }
          };

          return (
            <div className="bg-neutral-dark/30 rounded-lg p-4 mb-6 space-y-3">
              <h4 className="text-lg font-heading font-semibold text-brand-accent mb-3">
                {t('recharge.invoice.paymentInstructions')}
              </h4>
              
              {/* Reception Numbers - Display each number separately */}
              {receptionNumbers.length > 0 && (
                <div className="space-y-2">
                  <span className="text-neutral-medium font-body text-sm sm:text-base block mb-2">
                    {t('recharge.invoice.receptionNumber')}:
                  </span>
                  {receptionNumbers.map((number, index) => (
                    <div 
                      key={index}
                      className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-2 bg-neutral-dark/50 rounded-lg"
                    >
                      <span className="text-neutral-light font-body font-mono text-sm sm:text-base break-all">
                        {number}
                      </span>
                      <button
                        onClick={() => handleCopyReceptionNumber(number)}
                        className="p-1.5 bg-brand-accent/20 text-brand-accent rounded hover:bg-brand-accent/30 transition-colors flex-shrink-0 self-end sm:self-auto"
                        title={t('common.copy')}
                        aria-label={t('common.copy')}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {orderId && (
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-neutral-medium font-body text-sm sm:text-base">{t('recharge.invoice.orderId')}:</span>
                  <span className="text-neutral-light font-mono text-sm font-body break-all">{orderId}</span>
                </div>
              )}
              {message && (
                <p className="text-neutral-light font-body text-sm sm:text-base mt-3 p-3 bg-neutral-dark/50 rounded-lg break-words">
                  {message}
                </p>
              )}
            </div>
          );
        })()}

        {/* Syriatel Claim Button - Only show for Syriatel invoices */}
        {invoice.paymentMethod === 'Syriatel Cash' && invoice.status === 'pending' && (
          <div className="mb-4">
            <button
              onClick={() => setShowClaimDialog(true)}
              className="w-full px-6 py-3 bg-green-500/20 text-green-400 font-heading font-semibold rounded-lg hover:bg-green-500/30 border border-green-500/30 transform hover:scale-105 transition-all duration-300"
            >
              {t('recharge.syriatelClaim.button')}
            </button>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/balance"
            className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300 text-center"
          >
            {t('recharge.invoice.backToBalance')}
          </Link>
          <button
            onClick={() => {
              setInvoice(null);
              setError(null);
            }}
            className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
          >
            {t('recharge.invoice.createAnother')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('recharge.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('recharge.subtitle')}
            </p>
          </div>

          {/* Payment Method Selection */}
          {!invoice && (
            <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-6 animate-scale-in">
              <h2 className="text-2xl font-heading font-semibold text-brand-accent mb-4">
                {t('recharge.selectMethod')}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Payeer is temporarily disabled but code is kept for future use */}
                {(['crypto', 'syriatel', 'sham-cash', 'binance', 'coinex'] as PaymentMethod[]).map((method) => (
                  <button
                    key={method}
                    onClick={() => handleMethodChange(method)}
                    className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-lg font-heading font-semibold transition-all duration-300 ${
                      selectedMethod === method
                        ? 'bg-brand-accent text-brand-primary shadow-lg shadow-brand-accent/50 scale-105 border-2 border-brand-accent'
                        : 'bg-neutral-dark text-neutral-light hover:bg-neutral-dark/80 border-2 border-transparent hover:border-brand-accent/30'
                    }`}
                  >
                    <PaymentMethodIcon method={method} size="md" />
                    <span className="text-sm">{getPaymentMethodLabel(method)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Recharge Form */}
          {!invoice && (
            <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
              {selectedMethod === 'sham-cash' ? (
                <ShamCashClaimForm
                  onSubmit={handleClaimShamCashPayment}
                  isLoading={isShamClaiming}
                />
              ) : selectedMethod === 'binance' ? (
                <BinanceClaimForm
                  onSubmit={handleClaimBinancePayment}
                  isLoading={isBinanceClaiming}
                />
              ) : selectedMethod === 'coinex' ? (
                <CoinExClaimForm />
              ) : (
                <RechargeForm
                  paymentMethod={selectedMethod}
                  onSubmit={handleSubmit}
                  isLoading={isLoading}
                />
              )}

              {/* Error Message */}
              {error && (
                <div className="mt-6 p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down">
                  <p className="text-sm text-danger font-body">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Invoice Details */}
          {renderInvoiceDetails()}

          {/* Back Button */}
          {!invoice && (
            <div className="mt-6 text-center">
              <Link
                to="/balance"
                className="inline-block px-6 py-2 text-neutral-medium hover:text-neutral-light font-body transition-colors"
              >
                {t('recharge.backToBalance')}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Syriatel Claim Dialog */}
      {invoice && invoice.paymentMethod === 'Syriatel Cash' && (
        <SyriatelClaimDialog
          isOpen={showClaimDialog}
          onClose={() => setShowClaimDialog(false)}
          onClaim={handleClaimSyriatelPayment}
          isLoading={isClaiming}
          invoiceId={invoice.invoiceId}
        />
      )}
    </div>
  );
}

